import React from 'react';

import constants from 'constants/constants';
import { Controller, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';

import { useBasePath } from 'hooks/useBasePath';
import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';

import { IContract, IProject, useProjectTreeStore } from 'store/projectContract.store';

import { useMutationCreateContract } from 'api/mutations/createContract.mutation';

import { ProjectTreeNode, findeNodeByUUID } from 'components/common/TreeProgramFilter/ProjectTree/projectTree.utils';

import { executionConfig } from 'components/Dashboards/Project/ProjectWrapper/executionConfig';
import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';
import { TextInput } from 'components/DesignSystem/TextInput/TextInput';
import { CustomizedFormErrorMessage } from 'components/common';

import classes from './AddSubProgramModal.module.scss';

interface IAddSubProgramModalForm {
    name: string;
}

interface IAddSubProgramModal {
    showModal: boolean;
    onHideModal: () => void;
    callback?: (project: IProject | null, contract: IContract | null) => void;
}

export const AddSubProgramModal = ({ showModal, onHideModal, callback }: IAddSubProgramModal) => {
    const history = useHistory();
    const basePath = useBasePath();
    const { project, update, parentNode } = useProjectTreeStore();
    const { mutate: mutationCreateContract, isLoading: isLoadingCreateContract } = useMutationCreateContract();
    const { handleSnackBar } = useCustomSnackBar();
    const queryClient = useQueryClient();
    const {
        handleSubmit,
        setValue,
        control,
        formState: { errors },
        reset,
    } = useForm<IAddSubProgramModalForm>({
        defaultValues: {
            name: '',
        },
    });

    const handleSaveContractCallback = ({
        projectUid,
        contractUid,
        newData,
    }: {
        projectUid: ProjectTreeNode['uuid'];
        contractUid: ProjectTreeNode['uuid'];
        newData?: ProjectTreeNode;
    }) => {
        const projectNode = newData && projectUid ? findeNodeByUUID(newData, projectUid) : null;
        const contractNode = newData && contractUid ? findeNodeByUUID(newData, contractUid) : null;

        if (projectNode && contractNode) {
            update({
                project: {
                    id: projectNode.id,
                    title: projectNode.name,
                    uuid: projectNode.uuid,
                },
                contract: {
                    id: contractNode.id,
                    title: contractNode.name,
                    uuid: contractNode.uuid,
                },
            });
            if (callback) {
                callback(
                    {
                        id: projectNode.id,
                        title: projectNode.name,
                        uuid: projectNode.uuid,
                    },
                    {
                        id: contractNode.id,
                        title: contractNode.name,
                        uuid: contractNode.uuid,
                    },
                );
            }
        }
        update({ parentNode: null });
    };

    const handleSaveContract = (value: string) => {
        if (parentNode) {
            mutationCreateContract(
                {
                    contractName: value,
                    projectId: parentNode.id,
                },
                {
                    onSuccess: (response) => {
                        onHideModal();
                        Promise.all([
                            queryClient.invalidateQueries('getGeneralHierarchyList'),
                            queryClient.invalidateQueries('GetProgramGeneralHierarchyList'),
                        ]).then(() => {
                            const newData = queryClient.getQueryData<ProjectTreeNode>('getGeneralHierarchyList');
                            handleSaveContractCallback({
                                projectUid: parentNode.uuid,
                                contractUid: response.contractUid,
                                newData,
                            });
                            handleSnackBar('Sub-Program created successfully', 'success');

                            // should return to base url to show the data for the new contract
                            // not for program Library because we need to add the new project and contract ID's to the url
                            if (
                                basePath !==
                                `/${AppRoutes.DASHBOARD}/${AppRoutes.EXECUTION}/${executionConfig.programLibrary.link}`
                            ) {
                                history.push(basePath);
                            }
                        });
                    },
                    onError: (error) =>
                        DEFAULT_ERROR({
                            handleError: handleSnackBar,
                            message: `'${value}' ${error.response?.data}`,
                        }),
                },
            );
        }
    };

    const handleSaveUseForm = (value) => {
        handleSaveContract(value.name);
        closeModal();
    };

    const closeModal = () => {
        reset();
        onHideModal();
    };

    return (
        <Modal
            open={showModal}
            onClose={closeModal}
            title={'Add Sub-Program'}
            actions={
                <>
                    <Button size={'large'} variant={'secondary'} onClick={closeModal}>
                        Cancel
                    </Button>

                    <Button
                        type={'submit'}
                        size={'large'}
                        variant={'primary'}
                        isLoading={isLoadingCreateContract}
                        disabled={isLoadingCreateContract}
                        onClick={handleSubmit(handleSaveUseForm)}
                    >
                        Save
                    </Button>
                </>
            }
        >
            <div className={classes.addSubProgramModalContainer}>
                <form onSubmit={handleSubmit(handleSaveUseForm)} autoComplete={'off'}>
                    <div className={classes.inputTitle}>Project: {project?.title}</div>
                    <div className={classes.inputTitle}>Name *</div>

                    <div className={classes.inputWrapper}>
                        <Controller
                            render={({ field }) => (
                                <TextInput
                                    {...field}
                                    placeholder={'Add text...'}
                                    maxLength={constants.MAX_SUB_PROGRAM_NAME_LENGTH}
                                    onClear={() => {
                                        setValue('name', '');
                                    }}
                                />
                            )}
                            name={`name`}
                            control={control}
                            rules={{
                                required: 'Name is Required',
                                minLength: {
                                    value: 2,
                                    message: 'Name is too short',
                                },
                                pattern: {
                                    value: /^(?!\s+$).*/,
                                    message: 'Name cannot be empty',
                                },
                                validate: {
                                    notValidName: (value) =>
                                        value.toLowerCase() !== 'all' || '"All" is not a valid sub-program name.',
                                },
                            }}
                        />
                    </div>

                    {errors.name && <CustomizedFormErrorMessage text={errors.name.message} />}
                </form>
            </div>
        </Modal>
    );
};
